import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RotateCcw, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function CameraCapture({ open, onOpenChange, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [shot, setShot] = useState(null); // { url, blob }
  const [error, setError] = useState("");

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  const start = useCallback(async () => {
    setError("");
    setShot(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Your browser does not support camera access.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch {
      setError("Unable to access the camera. Please allow camera permission (and make sure a camera is connected).");
    }
  }, []);

  useEffect(() => {
    if (open) start();
    else { stop(); setShot(null); }
    return () => stop();
  }, [open, start, stop]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return toast.error("Camera not ready yet");
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return toast.error("Capture failed");
      setShot({ url: URL.createObjectURL(blob), blob });
      stop();
    }, "image/jpeg", 0.85);
  };

  const retake = () => { setShot(null); start(); };

  const use = () => {
    if (!shot) return;
    onCapture(new File([shot.blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="camera-dialog">
        <DialogHeader><DialogTitle>Take a photo</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {error ? (
            <p className="text-sm text-destructive" data-testid="camera-error">{error}</p>
          ) : shot ? (
            <img src={shot.url} alt="Captured receipt" className="w-full rounded-md border border-border" data-testid="camera-shot" />
          ) : (
            <div className="relative bg-black rounded-md overflow-hidden aspect-video">
              <video ref={videoRef} playsInline muted className="w-full h-full object-contain" data-testid="camera-video" />
              {!ready && (
                <div className="absolute inset-0 grid place-items-center text-white/80">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            {shot ? (
              <>
                <Button variant="outline" className="rounded-sm" onClick={retake} data-testid="camera-retake">
                  <RotateCcw className="h-4 w-4 mr-2" />Retake
                </Button>
                <Button className="rounded-sm" onClick={use} data-testid="camera-use">
                  <Check className="h-4 w-4 mr-2" />Use Photo
                </Button>
              </>
            ) : (
              <Button className="rounded-sm" onClick={capture} disabled={!ready || !!error} data-testid="camera-capture">
                <Camera className="h-4 w-4 mr-2" />Capture
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
